import { useCallback, useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme,
  Box,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch } from 'store';
import {
  type AtendimentoInterfaceV2,
  setHistoricoAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import { fetchComentarios, resetComentarios } from 'store/reducers/comentarios';
import { ContatoInterfaceV2 } from 'store/reducers/contatos';
import {
  addMensagemFinalizada,
  AtualizarMensagemEditadaAtendimentoInterface,
  atualizarMensagemEditadaFinalizadas,
  AtualizarMidiaMensagemInterface,
  atualizarMidiaMensagensFinalizadas,
  atualizarReacoesMensagemFinalizada,
  AtualizarReacoesMensagemInterface,
  atualizarStatusMensagensFinalizadas,
  fetchMensagensFinalizadasByAtendimento,
  MensagemInterface,
  resetHistoricoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensFinalizadas,
  resetSearchQuery,
  setCursorAConsumir,
  setMensagemApagadaFinalizadas,
  StatusMensagemType,
} from 'store/reducers/mensagens';

import InformacoesGridItem from './InformacoesGridItem';
import MessagesGridItem from './MessagesGridItem';

const useStyles = makeStyles(({ palette, spacing }) => ({
  dialogPaper: {
    height: '90%',
    width: '80%',
    overflow: 'hidden',
  },

  dialogContent: {
    padding: 0,
    margin: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:first-child': {
      paddingTop: 0,
    },
  },

  gridContainer: {
    height: '100%',
    width: '100%',
    margin: 0,
  },

  closeButton: {
    position: 'absolute',
    top: spacing(1),
    right: spacing(1),
    backgroundColor: palette.background.paper,
    zIndex: 2,

    '&:hover': {
      backgroundColor: palette.background.tertiary,
    },
  },
  tab: {
    borderTop: `1px solid ${palette.divider}`,
    '& .MuiTab-root': {
      minWidth: 'auto',
    },
  },
}));

interface ModalHistoricoAtendimentoProps {
  atendimento: AtendimentoInterfaceV2;
  open: boolean;
  onClose: () => void;
}

export default function ModalHistoricoAtendimento({
  atendimento,
  open,
  onClose,
}: ModalHistoricoAtendimentoProps) {
  const classes = useStyles();
  const [initialLoadingMessages, setInitialLoadingMessages] = useState(false);
  const contato = atendimento.contact as ContatoInterfaceV2;
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleResetLista = useCallback(() => {
    const atendimentoId = atendimento?.id;

    if (atendimentoId) {
      setInitialLoadingMessages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetHistoricoCursorAConsumir());
      dispatch(resetMensagensFinalizadas());
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
        })
        .finally(() => {
          setInitialLoadingMessages(false);
        });
    }
  }, [atendimento?.id, dispatch]);

  useEffect(() => {
    const handleReceberMensagem = (msgRecebida: MensagemInterface) => {
      if (msgRecebida.atendimentoId === atendimento?.id) {
        dispatch(addMensagemFinalizada(msgRecebida));
      }
    };

    const onReacaoMensagem = (data: AtualizarReacoesMensagemInterface) => {
      dispatch(atualizarReacoesMensagemFinalizada(data));
    };

    const onEdicaoMensagem = (
      data: AtualizarMensagemEditadaAtendimentoInterface,
    ) => {
      dispatch(atualizarMensagemEditadaFinalizadas(data));
    };

    const handleMensagemApagada = (data: {
      mensagemId: string;
      apagadaEm: string;
    }) => {
      dispatch(setMensagemApagadaFinalizadas(data));
    };

    const handleMensagemStatus = (data: {
      ackStatus: StatusMensagemType;
      ackMotivoErro: string;
      ackDescricaoErro: string;
      ids: string[];
    }) => {
      dispatch(atualizarStatusMensagensFinalizadas(data));
    };

    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      dispatch(atualizarMidiaMensagensFinalizadas(data));
    };

    socket.on(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
    socket.on(socketEvents.ON_RECEBER_MENSAGEM, handleReceberMensagem);
    socket.on(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
    socket.on(socketEvents.ON_MENSAGEM_APAGADA, handleMensagemApagada);
    socket.on(socketEvents.ON_MENSAGEM_STATUS, handleMensagemStatus);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(socketEvents.ON_RECEBER_MENSAGEM, handleReceberMensagem);
      socket.off(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
      socket.off(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
      socket.off(socketEvents.ON_MENSAGEM_APAGADA, handleMensagemApagada);
      socket.off(socketEvents.ON_MENSAGEM_STATUS, handleMensagemStatus);
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [atendimento?.id, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      TransitionProps={{
        onEnter: () => {
          if (atendimento) {
            dispatch(fetchComentarios({ atendimentoId: atendimento?.id }));
            dispatch(setHistoricoAtendimentoSelecionado(atendimento));
            handleResetLista();
          }
        },
        onExited: () => {
          dispatch(resetComentarios());
          dispatch(setHistoricoAtendimentoSelecionado(undefined));
          dispatch(resetSearchQuery());
        },
      }}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogContent className={classes.dialogContent}>
        <Box
          display="flex"
          flexDirection={isSmallScreen ? 'column' : 'row'}
          height="100%"
        >
          <IconButton onClick={onClose} className={classes.closeButton}>
            <Close />
          </IconButton>

          <Box
            style={{
              width: isSmallScreen ? '100%' : '25%',
              display: 'flex',
              flexDirection: 'column',
              flex: isSmallScreen ? 1 : undefined,
              height: isSmallScreen ? undefined : '100%',
              minHeight: 0,
              borderRight: isSmallScreen
                ? 'none'
                : `1px solid ${theme.palette.divider}`,
              position: 'relative',
            }}
          >
            <InformacoesGridItem
              contatoId={contato.id}
              initialLoadingMessages={initialLoadingMessages}
              onReset={handleResetLista}
            />
          </Box>

          {!isSmallScreen && (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              height="100%"
              style={{ minHeight: 0 }}
            >
              <Box
                flex={1}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                <MessagesGridItem
                  atendimento={atendimento}
                  contato={contato}
                  initialLoadingMessages={initialLoadingMessages}
                  onReset={handleResetLista}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
